/**
 * Parent grouping and z-order sort for React Flow paint order. Used for
 * `orderNodesForReactFlow` and for bring-to-front / send-to-back (`useCanvasZOrderActions`).
 */

import type { Node } from "@xyflow/react";

export const ROOT_PARENT_KEY = "";

export function nodeParentKey(node: Node, idSet: Set<string>): string {
  const p = node.parentId;
  if (p !== undefined && p !== null && p !== "" && idSet.has(p)) return p;
  return ROOT_PARENT_KEY;
}

/** z value used for stacking comparisons (sections default behind at -1). */
export function nodeStackingZ(node: Node): number {
  if (node.type === "section") return node.zIndex ?? -1;
  return node.zIndex ?? 0;
}

/** Lower z / lower id paints further back. */
export function compareCanvasSiblings(a: Node, b: Node): number {
  const dz = nodeStackingZ(a) - nodeStackingZ(b);
  if (dz !== 0) return dz;
  return a.id.localeCompare(b.id);
}

export function groupNodesByParentKey(
  nodes: Node[],
  idSet: Set<string>,
): Map<string, Node[]> {
  const map = new Map<string, Node[]>();
  for (const n of nodes) {
    const key = nodeParentKey(n, idSet);
    const list = map.get(key) ?? [];
    list.push(n);
    map.set(key, list);
  }
  return map;
}

function buildChildrenByParent(nodes: Node[]): Map<string, Node[]> {
  const idSet = new Set(nodes.map((n) => n.id));
  const byParent = groupNodesByParentKey(nodes, idSet);

  for (const list of byParent.values()) {
    list.sort(compareCanvasSiblings);
  }

  return byParent;
}

/**
 * Parent before descendants; among siblings sort by zIndex (asc) then id.
 * Later siblings / higher zIndex paint on top in React Flow.
 */
export function orderNodesForReactFlow(nodes: Node[]): Node[] {
  const byParent = buildChildrenByParent(nodes);
  const result: Node[] = [];

  function visit(node: Node) {
    result.push(node);
    const kids = byParent.get(node.id) ?? [];
    for (const k of kids) visit(k);
  }

  const roots = byParent.get(ROOT_PARENT_KEY) ?? [];
  for (const r of roots) visit(r);

  const seen = new Set(result.map((n) => n.id));
  for (const n of nodes) {
    if (!seen.has(n.id)) {
      result.push(n);
      seen.add(n.id);
    }
  }

  return result;
}
